import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Globe, FileText, RefreshCw, Code2, Heart, Store } from 'lucide-react';
import {
  useGetOnboardingQuery,
  useUpdateOnboardingMutation,
} from '../../../store/api/onboardingApi';
import WizardCard from '../WizardCard';
import SelectableCard from '../SelectableCard';

// Use cases + the activation path each maps to (workflow.md routing table).
const USE_CASES = [
  { id: 'website', label: 'Accept payments on my website', icon: Globe, path: 'api' },
  { id: 'invoices', label: 'Send invoices to clients', icon: FileText, path: 'invoice' },
  { id: 'subscriptions', label: 'Recurring / subscription billing', icon: RefreshCw, path: 'api' },
  { id: 'api', label: 'Integrate via API', icon: Code2, path: 'api' },
  { id: 'donations', label: 'Accept donations', icon: Heart, path: 'invoice' },
  { id: 'marketplace', label: 'Marketplace payouts', icon: Store, path: 'api' },
];

const VOLUME_RANGES = [
  { value: '', label: 'Select a range' },
  { value: '0-1k', label: '$0 - $1,000' },
  { value: '1k-10k', label: '$1,000 - $10,000' },
  { value: '10k-50k', label: '$10,000 - $50,000' },
  { value: '50k+', label: '$50,000+' },
];

const SOURCES = [
  { value: '', label: 'Select an option' },
  { value: 'search', label: 'Search engine' },
  { value: 'social', label: 'Social media' },
  { value: 'referral', label: 'Friend or colleague' },
  { value: 'content', label: 'Blog or article' },
  { value: 'other', label: 'Other' },
];

const pathForPrimary = (primaryId) =>
  USE_CASES.find((u) => u.id === primaryId)?.path || 'invoice';

const UseCases = () => {
  const navigate = useNavigate();
  const { data: state, isLoading, isError } = useGetOnboardingQuery();
  const [updateOnboarding, { isLoading: isSaving }] = useUpdateOnboardingMutation();

  const [selected, setSelected] = useState([]);
  const [primary, setPrimary] = useState(null);
  const [volume, setVolume] = useState('');
  const [source, setSource] = useState('');

  // Prefill from saved state (resume).
  useEffect(() => {
    if (!state) return;
    setSelected(state.use_cases || []);
    setPrimary(state.primary_use_case || null);
    setVolume(state.volume || '');
    setSource(state.source || '');
  }, [state]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      // Keep primary valid: clear if deselected, default to the only/first pick.
      setPrimary((cur) => {
        if (next.length === 0) return null;
        if (cur && next.includes(cur)) return cur;
        return next[0];
      });
      return next;
    });
  };

  const save = async (patch) => {
    try {
      await updateOnboarding(patch).unwrap();
      navigate('/onboarding/plan');
    } catch (err) {
      toast.error(err?.message || 'Could not save. Please try again.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const primaryId = selected.length === 1 ? selected[0] : primary;
    save({
      use_cases: selected,
      primary_use_case: primaryId,
      onboarding_path: pathForPrimary(primaryId),
      volume,
      source,
      onboarding_status: 'usecases_done',
    });
  };

  // Skip defaults the primary to invoicing (Path A), per workflow.md edge case.
  const handleSkip = () =>
    save({
      use_cases: [],
      primary_use_case: 'invoices',
      onboarding_path: 'invoice',
      onboarding_status: 'usecases_done',
    });

  if (isLoading) {
    return (
      <section className="wiz-card" aria-busy="true">
        <div className="wiz-skeleton" style={{ width: '80%', marginBottom: 12 }} />
        <div className="wiz-skeleton" style={{ width: '60%', marginBottom: 24 }} />
        <div className="wiz-usecase-grid">
          {USE_CASES.map((u) => (
            <div key={u.id} className="wiz-skeleton block" />
          ))}
        </div>
      </section>
    );
  }

  const showPrimaryRow = selected.length > 1;

  return (
    <WizardCard
      title="What do you want to do with MaxelPay?"
      subtitle="Pick all that apply. We will set you up for the first one."
      primaryLabel="Continue"
      onSubmit={handleSubmit}
      primaryDisabled={selected.length === 0}
      primaryLoading={isSaving}
      onBack={() => navigate('/onboarding/business')}
      onSkip={handleSkip}
      error={isError ? 'We could not load your saved choices. You can still continue.' : undefined}
    >
      <fieldset className="wiz-fieldset">
        <legend className="wiz-visually-hidden">Use cases</legend>
        <div className="wiz-usecase-grid">
          {USE_CASES.map((u) => {
            const Icon = u.icon;
            return (
              <SelectableCard
                key={u.id}
                type="checkbox"
                name="use-cases"
                value={u.id}
                checked={selected.includes(u.id)}
                onChange={toggle}
                icon={<Icon size={20} />}
                label={u.label}
              />
            );
          })}
        </div>
      </fieldset>

      {showPrimaryRow && (
        <fieldset className="wiz-fieldset wiz-primary-row">
          <legend className="wiz-primary-legend">Which is most important?</legend>
          <div className="wiz-primary-options">
            {selected.map((id) => {
              const u = USE_CASES.find((x) => x.id === id);
              return (
                <SelectableCard
                  key={id}
                  type="radio"
                  name="primary-use-case"
                  value={id}
                  checked={primary === id}
                  onChange={setPrimary}
                  label={u?.label || id}
                  compact
                />
              );
            })}
          </div>
        </fieldset>
      )}

      <div className="wiz-optional">
        <div className="wiz-optional-divider">Optional, helps us tailor things</div>
        <div className="rd-field wiz-field">
          <label className="rd-label" htmlFor="uc-volume">Expected monthly volume</label>
          <select
            id="uc-volume"
            className="rd-select"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
          >
            {VOLUME_RANGES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="rd-field wiz-field">
          <label className="rd-label" htmlFor="uc-source">How did you hear about us?</label>
          <select
            id="uc-source"
            className="rd-select"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            {SOURCES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>
    </WizardCard>
  );
};

export default UseCases;
